/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { VSBuffer } from '../../../base/common/buffer.js';
import { Event } from '../../../base/common/event.js';
import { URI, UriComponents } from '../../../base/common/uri.js';
import { MessageBoxOptions, MessageBoxReturnValue, OpenDevToolsOptions, OpenDialogOptions, OpenDialogReturnValue, SaveDialogOptions, SaveDialogReturnValue } from '../../../base/parts/sandbox/common/electronTypes.js';
import { ISerializableCommandAction } from '../../action/common/action.js';
import { INativeOpenDialogOptions } from '../../dialogs/common/dialogs.js';
import { createDecorator } from '../../instantiation/common/instantiation.js';
import { IV8Profile } from '../../profiling/common/profiling.js';
import { AuthInfo, Credentials } from '../../request/common/request.js';
import { IPartsSplash } from '../../theme/common/themeService.js';
import { AgentsWindowOpenSource, IAgentsWindowDraft, IColorScheme, IOpenedAuxiliaryWindow, IOpenedMainWindow, IOpenEmptyWindowOptions, IOpenWindowOptions, IPoint, IRectangle, IWindowOpenable } from '../../window/common/window.js';

export interface IToastOptions {
	readonly id: string;
	/**
	 * Prevents another live toast with the same key from being shown.
	 */
	readonly dedupeKey?: string;

	readonly title: string;
	readonly body?: string;

	readonly actions?: readonly string[];

	readonly silent?: boolean;
}

export interface IToastResult {
	readonly supported: boolean;
	/**
	 * True when a live toast with the requested dedupe key is already visible.
	 */
	readonly suppressed?: boolean;

	readonly clicked: boolean;
	readonly actionIndex?: number;
}

/**
 * A count badge to render on the application icon in the dock (macOS), the
 * launcher (Linux) or over the taskbar icon of a window (Windows).
 */
export interface IApplicationBadge {

	/** The number to show. A count of `0` clears the badge. */
	readonly count: number;

	/** Accessible description of the badge, used for the Windows taskbar overlay. */
	readonly description: string;

	/**
	 * PNG image data as a `data:` URL for the Windows taskbar overlay icon,
	 * which has no built-in count rendering. Ignored on other platforms,
	 * where the OS renders {@link count} itself.
	 */
	readonly iconDataURL?: string;
}

/**
 * A ZIP entry whose contents are inline or streamed from a local file.
 */
export type INativeZipFile =
	| { readonly path: string; readonly contents: string }
	| {
		readonly path: string;
		readonly source: URI;
		readonly size: number;
		/** Skip this entry when its source cannot be opened or inspected. */
		readonly skipSourceErrors?: boolean;
	}
	| { readonly sourceArchive: URI };

export interface INativeZipOptions {
	readonly maxSize?: number;
	readonly maxEntries: number;
}

export interface IOpenAgentsWindowOptions {
	readonly folderUri?: UriComponents;
	/** Use the invoking editor's folder only for a fresh composer, without replacing an existing session or user choice. */
	readonly folderUriIsDefault?: boolean;
	readonly sessionResource?: UriComponents;
	readonly source?: AgentsWindowOpenSource;
	readonly draft?: IAgentsWindowDraft;
}

export interface ICPUProperties {
	model: string;
	speed: number;
}

export interface IOSProperties {
	type: string;
	release: string;
	arch: string;
	platform: string;
	cpus: ICPUProperties[];
}

export interface IOSStatistics {
	totalmem: number;
	freemem: number;
	loadavg: number[];
}

export interface IOSProxy {
	readonly kind: 'direct' | 'http' | 'socks';
	readonly host?: string;
}

export interface IOSProxyEnvironmentVariableStatus {
	readonly variable: string;
	readonly value: string;
	readonly error?: string;
}

export interface IOSProxyPacSourceStatus {
	readonly state: 'disabled' | 'unsupported' | 'unconfigured' | 'not-found' | 'available' | 'error-discovery' | 'error-download' | 'unknown';
	readonly url?: string;
	readonly error?: string;
}

export interface IOSProxyConfig {
	readonly environment: {
		readonly httpProxy?: IOSProxyEnvironmentVariableStatus;
		readonly httpsProxy?: IOSProxyEnvironmentVariableStatus;
		readonly allProxy?: IOSProxyEnvironmentVariableStatus;
		readonly noProxy?: IOSProxyEnvironmentVariableStatus;
	};
	readonly autoDetect: boolean;
	readonly pacUrl?: string;
	readonly pac?: {
		readonly url: string;
		readonly content: string;
		readonly source: 'wpad-dns' | 'wpad-dhcp' | 'configured' | 'unknown';
	};
	readonly wpadDhcp: IOSProxyPacSourceStatus;
	readonly wpadDns: IOSProxyPacSourceStatus;
	readonly configuredPac: IOSProxyPacSourceStatus;
	readonly staticRules?: {
		readonly http?: IOSProxy;
		readonly https?: IOSProxy;
		readonly socks?: IOSProxy;
	};
	readonly platform?:
	| { readonly kind: 'windows'; readonly proxy?: string; readonly proxyBypass?: string }
	| { readonly kind: 'macos'; readonly exceptions: readonly string[]; readonly excludeSimpleHostnames: boolean }
	| { readonly kind: 'linux'; readonly mode?: string; readonly ignoreHosts: readonly string[] }
	| { readonly kind: 'unknown' };
}

export interface INativeHostOptions {
	readonly targetWindowId?: number;
}

export interface IStartTracingOptions {

	/**
	 * Whether to enable heap profiling for MemoryInfra traces. Only takes effect
	 * if the `disabled-by-default-memory-infra` category is included in the trace
	 * and requires the recording to also collect periodic memory dumps.
	 */
	readonly enableHeapProfiling?: boolean;
}

export const enum FocusMode {

	/**
	 * (Default) Transfer focus to the target window
	 * when the editor is focused.
	 */
	Transfer,

	/**
	 * Transfer focus to the target window when the
	 * editor is focused, otherwise notify the user that
	 * the app has activity (macOS/Windows only).
	 */
	Notify,

	/**
	 * Force the window to be focused, even if the editor
	 * is not currently focused.
	 */
	Force,
}

export interface INativeSystemWideKeybinding {

	/**
	 * The keybinding in Electron accelerator format (e.g. `Control+Cmd+A`).
	 * See https://www.electronjs.org/docs/latest/api/accelerator.
	 */
	readonly accelerator: string;

	/**
	 * The command to execute when the global shortcut is triggered.
	 */
	readonly commandId: string;

	/**
	 * Optional command arguments as configured in `keybindings.json`. Must be JSON-serializable.
	 */
	readonly args?: unknown;

	/**
	 * The user settings label (e.g. `ctrl+cmd+a`) for diagnostics/notifications.
	 */
	readonly userSettingsLabel?: string;
}

export interface INativeSystemWideKeybindingResult {

	/**
	 * The user settings labels (or accelerators) that could not be registered, e.g. because the
	 * accelerator is already taken by the OS or another application.
	 */
	readonly failed: string[];
}

export interface ICommonNativeHostService {

	readonly _serviceBrand: undefined;

	// Properties
	readonly windowId: number;

	// Events
	readonly onDidOpenMainWindow: Event<number>;

	readonly onDidMaximizeWindow: Event<number>;
	readonly onDidUnmaximizeWindow: Event<number>;

	readonly onDidFocusMainWindow: Event<number>;
	readonly onDidBlurMainWindow: Event<number>;

	readonly onDidChangeWindowFullScreen: Event<{ windowId: number; fullscreen: boolean }>;
	readonly onDidChangeWindowAlwaysOnTop: Event<{ windowId: number; alwaysOnTop: boolean }>;

	readonly onDidFocusMainOrAuxiliaryWindow: Event<number>;
	readonly onDidBlurMainOrAuxiliaryWindow: Event<number>;

	readonly onDidChangeDisplay: Event<void>;

	readonly onDidSuspendOS: Event<void>;
	readonly onDidResumeOS: Event<unknown>;

	readonly onDidChangeOnBatteryPower: Event<boolean>;
	readonly onDidChangeThermalState: Event<ThermalState>;
	readonly onDidChangeSpeedLimit: Event<number>;
	readonly onWillShutdownOS: Event<void>;
	readonly onDidLockScreen: Event<void>;
	readonly onDidUnlockScreen: Event<void>;

	readonly onDidChangeColorScheme: Event<IColorScheme>;

	readonly onDidChangePassword: Event<{ readonly service: string; readonly account: string }>;

	readonly onDidTriggerWindowSystemContextMenu: Event<{ readonly windowId: number; readonly x: number; readonly y: number }>;

	// Window
	getWindows(options: { includeAuxiliaryWindows: true }): Promise<Array<IOpenedMainWindow | IOpenedAuxiliaryWindow>>;
	getWindows(options: { includeAuxiliaryWindows: false }): Promise<Array<IOpenedMainWindow>>;
	getWindowCount(): Promise<number>;
	getActiveWindowId(): Promise<number | undefined>;
	getActiveWindowPosition(): Promise<IRectangle | undefined>;
	getNativeWindowHandle(windowId: number): Promise<VSBuffer | undefined>;

	openWindow(options?: IOpenEmptyWindowOptions): Promise<void>;
	openWindow(toOpen: IWindowOpenable[], options?: IOpenWindowOptions): Promise<void>;

	openAgentsWindow(options?: IOpenAgentsWindowOptions): Promise<void>;

	/**
	 * Registers this window's set of system-wide (OS global) keybindings with the main process,
	 * replacing any previously registered by this window. The shortcuts fire even when the
	 * application is not focused. Returns the set that could not be registered.
	 */
	syncSystemWideKeybindings(keybindings: INativeSystemWideKeybinding[]): Promise<INativeSystemWideKeybindingResult>;

	isFullScreen(options?: INativeHostOptions): Promise<boolean>;
	toggleFullScreen(options?: INativeHostOptions): Promise<void>;

	getCursorScreenPoint(): Promise<{ readonly point: IPoint; readonly display: IRectangle }>;

	isMaximized(options?: INativeHostOptions): Promise<boolean>;
	maximizeWindow(options?: INativeHostOptions): Promise<void>;
	unmaximizeWindow(options?: INativeHostOptions): Promise<void>;
	minimizeWindow(options?: INativeHostOptions): Promise<void>;
	moveWindowTop(options?: INativeHostOptions): Promise<void>;
	positionWindow(position: IRectangle, options?: INativeHostOptions): Promise<void>;

	isWindowAlwaysOnTop(options?: INativeHostOptions): Promise<boolean>;
	toggleWindowAlwaysOnTop(options?: INativeHostOptions): Promise<void>;
	setWindowAlwaysOnTop(alwaysOnTop: boolean, options?: INativeHostOptions): Promise<void>;

	updateWindowControls(options: INativeHostOptions & { height?: number; backgroundColor?: string; foregroundColor?: string; dimmed?: boolean }): Promise<void>;

	updateWindowAccentColor(color: 'default' | 'off' | string, inactiveColor: string | undefined): Promise<void>;

	setMinimumSize(width: number | undefined, height: number | undefined): Promise<void>;

	saveWindowSplash(splash: IPartsSplash): Promise<void>;

	setBackgroundThrottling(allowed: boolean): Promise<void>;

	/**
	 * Make the window focused.
	 * @param options specify the specific window to focus and the focus mode.
	 * Defaults to {@link FocusMode.Transfer}.
	 */
	focusWindow(options?: INativeHostOptions & { mode?: FocusMode }): Promise<void>;

	// Dialogs
	showMessageBox(options: MessageBoxOptions & INativeHostOptions): Promise<MessageBoxReturnValue>;
	showSaveDialog(options: SaveDialogOptions & INativeHostOptions): Promise<SaveDialogReturnValue>;
	showOpenDialog(options: OpenDialogOptions & INativeHostOptions): Promise<OpenDialogReturnValue>;

	pickFileFolderAndOpen(options: INativeOpenDialogOptions): Promise<void>;
	pickFileAndOpen(options: INativeOpenDialogOptions): Promise<void>;
	pickFolderAndOpen(options: INativeOpenDialogOptions): Promise<void>;
	pickWorkspaceAndOpen(options: INativeOpenDialogOptions): Promise<void>;

	// OS
	showItemInFolder(path: string): Promise<void>;
	setRepresentedFilename(path: string, options?: INativeHostOptions): Promise<void>;
	setDocumentEdited(edited: boolean, options?: INativeHostOptions): Promise<void>;
	/**
	 * Renders a count badge on the application icon. Passing `undefined` or a
	 * badge with a count of `0` clears it again.
	 */
	setApplicationBadge(badge: IApplicationBadge | undefined, options?: INativeHostOptions): Promise<void>;
	openExternal(url: string, defaultApplication?: string): Promise<boolean>;
	moveItemToTrash(fullPath: string): Promise<void>;

	getMediaAccessStatus(mediaType: 'microphone' | 'camera' | 'screen'): Promise<'not-determined' | 'granted' | 'denied' | 'restricted' | 'unknown'>;

	isAdmin(): Promise<boolean>;
	writeElevated(source: URI, target: URI, options?: { unlock?: boolean }): Promise<void>;
	isRunningUnderARM64Translation(): Promise<boolean>;

	getOSProperties(): Promise<IOSProperties>;
	getOSStatistics(): Promise<IOSStatistics>;
	getOSVirtualMachineHint(): Promise<number>;

	getOSColorScheme(): Promise<IColorScheme>;

	hasWSLFeatureInstalled(): Promise<boolean>;

	// Screenshots
	getScreenshot(rect?: IRectangle): Promise<VSBuffer | undefined>;

	// GitHub mobile upload API (runs in main process to avoid CORS)
	uploadFileViaMobileApi(token: string, repoId: string, fileName: string, fileBytes: VSBuffer, contentType: string): Promise<{ fileName: string; assetUrl: string; contentType: string }>;

	// Process
	getProcessId(): Promise<number | undefined>;
	killProcess(pid: number, code: string): Promise<void>;

	// Clipboard
	triggerPaste(options?: INativeHostOptions): Promise<void>;
	readClipboardText(type?: 'selection' | 'clipboard'): Promise<string>;
	writeClipboardText(text: string, type?: 'selection' | 'clipboard'): Promise<void>;
	readClipboardFindText(): Promise<string>;
	writeClipboardFindText(text: string): Promise<void>;
	writeClipboardBuffer(format: string, buffer: VSBuffer, type?: 'selection' | 'clipboard'): Promise<void>;
	readClipboardBuffer(format: string): Promise<VSBuffer>;
	hasClipboard(format: string, type?: 'selection' | 'clipboard'): Promise<boolean>;
	readImage(): Promise<Uint8Array>;

	// macOS Touchbar
	newWindowTab(): Promise<void>;
	showPreviousWindowTab(): Promise<void>;
	showNextWindowTab(): Promise<void>;
	moveWindowTabToNewWindow(): Promise<void>;
	mergeAllWindowTabs(): Promise<void>;
	toggleWindowTabsBar(): Promise<void>;
	updateTouchBar(items: ISerializableCommandAction[][]): Promise<void>;

	// macOS Shell command
	installShellCommand(): Promise<void>;
	uninstallShellCommand(): Promise<void>;

	// Lifecycle
	notifyReady(): Promise<void>;
	relaunch(options?: { addArgs?: string[]; removeArgs?: string[] }): Promise<void>;
	reload(options?: { disableExtensions?: boolean }): Promise<void>;
	closeWindow(options?: INativeHostOptions): Promise<void>;
	quit(): Promise<void>;
	exit(code: number): Promise<void>;

	// Development
	openDevTools(options?: Partial<OpenDevToolsOptions> & INativeHostOptions): Promise<void>;
	toggleDevTools(options?: INativeHostOptions): Promise<void>;
	openGPUInfoWindow(): Promise<void>;
	openDevToolsWindow(url: string): Promise<void>;
	openContentTracingWindow(): Promise<void>;
	stopTracing(): Promise<void>;

	// Perf Introspection
	profileRenderer(session: string, duration: number): Promise<IV8Profile>;
	startTracing(categories: string, options?: IStartTracingOptions): Promise<void>;

	// Connectivity
	resolveProxy(url: string): Promise<string | undefined>;
	resolveProxyWithPackage(url: string): Promise<IOSProxy[]>;
	readProxyConfigWithPackage(): Promise<IOSProxyConfig>;
	lookupAuthorization(authInfo: AuthInfo): Promise<Credentials | undefined>;
	lookupKerberosAuthorization(url: string): Promise<string | undefined>;
	loadCertificates(): Promise<string[]>;
	isPortFree(port: number): Promise<boolean>;
	findFreePort(startPort: number, giveUpAfter: number, timeout: number, stride?: number): Promise<number>;

	// Registry (Windows only)
	windowsGetStringRegKey(hive: 'HKEY_CURRENT_USER' | 'HKEY_LOCAL_MACHINE' | 'HKEY_CLASSES_ROOT' | 'HKEY_USERS' | 'HKEY_CURRENT_CONFIG', path: string, name: string): Promise<string | undefined>;

	// Toast Notifications
	showToast(options: IToastOptions): Promise<IToastResult>;
	clearToast(id: string): Promise<void>;
	clearToasts(): Promise<void>;

	// Zip
	/**
	 * Creates a zip file at the specified path containing the provided files.
	 *
	 * @param zipPath The URI where the zip file should be created.
	 * @param files An array of file entries to include in the zip. Each entry has
	 * a relative path and is either given inline string `contents`, or a local
	 * file `source` URI together with the number of leading bytes (`size`) to
	 * stream from it.
	 */
	createZipFile(zipPath: URI, files: INativeZipFile[], options?: INativeZipOptions): Promise<void>;

	// Power
	getSystemIdleState(idleThreshold: number): Promise<SystemIdleState>;
	getSystemIdleTime(): Promise<number>;
	getCurrentThermalState(): Promise<ThermalState>;
	isOnBatteryPower(): Promise<boolean>;
	startPowerSaveBlocker(type: PowerSaveBlockerType): Promise<number>;
	stopPowerSaveBlocker(id: number): Promise<boolean>;
	isPowerSaveBlockerStarted(id: number): Promise<boolean>;
}

/**
 * Represents the system's idle state.
 */
export type SystemIdleState = 'active' | 'idle' | 'locked' | 'unknown';

/**
 * Represents the system's thermal state.
 */
export type ThermalState = 'unknown' | 'nominal' | 'fair' | 'serious' | 'critical';

/**
 * The type of power save blocker.
 */
export type PowerSaveBlockerType = 'prevent-app-suspension' | 'prevent-display-sleep';

export const INativeHostService = createDecorator<INativeHostService>('nativeHostService');

/**
 * A set of methods specific to a native host, i.e. unsupported in web
 * environments.
 *
 * @see {@link IHostService} for methods that can be used in native and web
 * hosts.
 */
export interface INativeHostService extends ICommonNativeHostService { }
